import React, { useEffect, useState, useRef } from 'react';
/**
 * PracticeHeaderMinimal — Sticky header bar for practice sessions.
 *
 * Shows progress (question X/Y), optional streak counter,
 * cheat sheet toggle, and exit button. Responsive for mobile.
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Target } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../../mascot';
interface PracticeHeaderMinimalProps {
  currentQuestion: number;
  totalQuestions: number;
  streak?: number;
  speedStreak?: number;
  isFluency?: boolean;
  elapsedMs?: number;
  onExit: () => void;
  onToggleCheatSheet?: () => void;
  isCheatSheetOpen?: boolean;
  nudgeActive?: boolean;
  learningGoal?: string;
}
export function PracticeHeaderMinimal({
  currentQuestion,
  totalQuestions,
  streak,
  speedStreak = 0,
  isFluency = false,
  elapsedMs,
  onExit,
  onToggleCheatSheet,
  isCheatSheetOpen = false,
  nudgeActive = false,
  learningGoal
}: PracticeHeaderMinimalProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const progress = currentQuestion / totalQuestions * 100;
  const [showGoalPopover, setShowGoalPopover] = useState(false);
  const goalRef = useRef<HTMLDivElement>(null);
  // Close popover on outside click or Escape key
  useEffect(() => {
    if (!showGoalPopover) return;
    function handleClick(e: MouseEvent) {
      if (goalRef.current && !goalRef.current.contains(e.target as Node)) {
        setShowGoalPopover(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowGoalPopover(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showGoalPopover]);
  return <header className={`border-b sticky top-0 z-40 shadow-sm ${isFluency ? 'bg-amber-50/80 border-amber-200' : 'bg-white border-slate-200'}`} data-id="element-6763">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3" data-id="element-6764">
        <div className="flex items-center justify-between gap-2 sm:gap-4" data-id="element-6765">
          {/* Left: Progress */}
          <div className="flex-1 max-w-xs sm:max-w-md" data-id="element-6766">
            <div className="flex items-center justify-between mb-1.5 sm:mb-2" data-id="element-6767">
              <span className="text-xs sm:text-sm font-bold text-slate-700 flex items-center gap-1.5" data-id="element-6768">
                {isFluency && <span className="inline-flex items-center gap-1 bg-amber-100 border border-amber-300 text-amber-700 text-xs font-black px-2 py-0.5 rounded-full" data-id="element-6769">
                    <Zap className="w-3 h-3 fill-amber-500 text-amber-500" data-id="element-6770" />
                    <span className="hidden sm:inline" data-id="element-6771">Snel & Zeker</span>
                  </span>}
                Vraag {currentQuestion}/{totalQuestions}
              </span>
              <div className="flex items-center gap-2" data-id="element-6772">
                {/* Fluency elapsed timer */}
                {isFluency && elapsedMs !== undefined && <span className="text-xs font-mono font-bold text-amber-600 tabular-nums" data-id="element-6773">
                    ⏱ {(elapsedMs / 1000).toFixed(1)}s
                  </span>}
                <span className="text-xs font-semibold text-slate-500" data-id="element-6774">
                  {Math.round(progress)}%
                </span>
              </div>
            </div>
            <div className="h-1.5 sm:h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-6775">
              <div className={`h-full rounded-full transition-all duration-300 ${isFluency ? 'bg-gradient-to-r from-amber-400 to-orange-400' : 'bg-gradient-to-r from-indigo-500 to-violet-500'}`} style={{
              width: `${progress}%`
            }} data-id="element-6776" />
            </div>
          </div>

          {/* Center: Streak (practice mode only, hidden on small mobile) */}
          <div className="hidden sm:flex items-center gap-2" data-id="element-6777">
            {isFluency && speedStreak >= 3 && <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-amber-100 border-amber-300" data-id="element-6778">
                <Zap className="w-4 h-4 text-orange-500 fill-orange-500" data-id="element-6779" />
                <span className="text-sm font-bold text-orange-700" data-id="element-6780">
                  {speedStreak}x Snelheidsreeks!
                </span>
              </div>}
            {!isFluency && streak !== undefined && streak > 0 && <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border ${streak >= 10 ? 'bg-amber-100 border-amber-300' : 'bg-amber-50 border-amber-200'}`} data-id="element-6781">
                <Zap className={`w-4 h-4 ${streak >= 10 ? 'text-orange-500 fill-orange-500' : 'text-amber-500 fill-amber-500'}`} data-id="element-6782" />
                <span className={`text-sm font-bold ${streak >= 10 ? 'text-orange-700' : 'text-amber-700'}`} data-id="element-6783">
                  {streak} op rij!
                </span>
              </div>}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-6784">
            {/* Learning Goal Icon Button */}
            {learningGoal && <div ref={goalRef} className="relative" data-id="element-6785">
                <button onClick={() => setShowGoalPopover(v => !v)} className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg transition-colors ${showGoalPopover ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-50 text-slate-400 hover:bg-indigo-50 hover:text-indigo-500'}`} aria-label="Bekijk leerdoel" aria-expanded={showGoalPopover} title="Leerdoel" data-id="element-6786">
                  <Target className="w-4 h-4" data-id="element-6787" />
                </button>
                <AnimatePresence data-id="element-6788">
                  {showGoalPopover && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 top-full mt-2 w-64 sm:w-72 bg-white rounded-xl shadow-lg border border-slate-200 p-3 z-50" data-id="element-6789">
                      <div className="flex items-start gap-2" data-id="element-6790">
                        <span className="text-indigo-400 text-sm mt-0.5" data-id="element-6791">
                          🎯
                        </span>
                        <div data-id="element-6792">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-400/80 mb-0.5" data-id="element-6793">
                            Leerdoel
                          </div>
                          <div className="text-sm font-medium text-slate-700 leading-snug" data-id="element-6794">
                            {learningGoal}
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>}

            {onToggleCheatSheet && <motion.div key={nudgeActive ? 'nudge-on' : 'nudge-off'} animate={nudgeActive && !isCheatSheetOpen ? {
            scale: [1, 1.06, 1],
            boxShadow: ['0 0 0 0px rgba(251, 191, 36, 0)', '0 0 0 5px rgba(251, 191, 36, 0.25)', '0 0 0 0px rgba(251, 191, 36, 0)']
          } : {
            scale: 1,
            boxShadow: '0 0 0 0px rgba(251, 191, 36, 0)'
          }} transition={nudgeActive && !isCheatSheetOpen ? {
            duration: 1.8,
            repeat: 2,
            ease: 'easeInOut'
          } : {
            duration: 0.3
          }} className="rounded-lg" data-id="element-6795">
                <Button onClick={onToggleCheatSheet} variant="secondary" size="sm" className={isCheatSheetOpen ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : ''} data-id="element-6796">
                  <motion.div animate={nudgeActive && !isCheatSheetOpen ? {
                y: [0, -2, 0],
                rotate: [0, -6, 6, 0]
              } : {
                y: 0,
                rotate: 0
              }} transition={nudgeActive && !isCheatSheetOpen ? {
                duration: 0.6,
                repeat: 5,
                ease: 'easeInOut',
                delay: 0.3
              } : {
                duration: 0.2
              }} data-id="element-6797">
                    <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} animate={false} data-id="element-6798" />
                  </motion.div>
                  <span className="hidden sm:inline ml-1" data-id="element-6799">Spieken</span>
                </Button>
              </motion.div>}

            <Button onClick={onExit} variant="secondary" size="sm" className="text-slate-600 hover:text-slate-900 px-2 sm:px-4" data-id="element-6800">
              <X className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6801" />
              <span className="hidden sm:inline ml-2" data-id="element-6802">Stoppen</span>
            </Button>
          </div>
        </div>
      </div>
    </header>;
}